import React from 'react';
import { motion } from 'framer-motion';
interface HexagonNumberProps {
  number: number;
  color?: string;
}
export const HexagonNumber = ({
  number,
  color = '#9CC31C'
}: HexagonNumberProps) => {
  return <div className="relative" data-id="element-114">
      <svg width="60" height="66" viewBox="0 0 60 66" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-115">
        <path d="M30 0L59.4 16.5V49.5L30 66L0.6 49.5V16.5L30 0Z" fill={color} fillOpacity="0.9" data-id="element-116" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-xl" data-id="element-117">
        {number}
      </div>
    </div>;
};
interface AngledDividerProps {
  color?: string;
  reversed?: boolean;
}
export const AngledDivider = ({
  color = '#9CC31C',
  reversed = false
}: AngledDividerProps) => {
  // Generate an array of hexagons with varying sizes
  const hexagons = Array.from({
    length: 15
  }, (_, i) => {
    // Base size between 12 and 24
    const baseSize = 12 + Math.floor(Math.random() * 12);
    // Opacity between 0.1 and 0.3
    const opacity = 0.1 + Math.random() * 0.2;
    // Position along the horizontal axis (0-100%)
    const position = i / 14 * 100;
    // Fade out effect - opacity multiplier based on position from center
    const distanceFromCenter = Math.abs(position - 50) / 50;
    const fadeMultiplier = 1 - Math.pow(distanceFromCenter, 1.5);
    return {
      size: baseSize,
      opacity: opacity * fadeMultiplier,
      position,
      rotation: Math.random() * 60 - 30,
      delay: Math.random() * 5 // Random animation delay
    };
  });
  return <div className="w-full h-24 flex items-center justify-center my-12 overflow-hidden" data-id="element-118">
      <div className="relative w-3/5 h-full" data-id="element-119">
        {hexagons.map((hexagon, index) => <motion.div key={index} className="absolute top-1/2 transform -translate-y-1/2" style={{
        left: `${hexagon.position}%`
      }} animate={{
        y: [0, -5, 0, 5, 0],
        rotate: [hexagon.rotation, hexagon.rotation + 10, hexagon.rotation]
      }} transition={{
        duration: 8 + Math.random() * 4,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
        delay: hexagon.delay
      }} data-id="element-120">
            <svg width={hexagon.size} height={hexagon.size * 1.15} viewBox="0 0 60 66" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-121">
              <path d="M30 0L59.4 16.5V49.5L30 66L0.6 49.5V16.5L30 0Z" fill={color} fillOpacity={hexagon.opacity} data-id="element-122" />
            </svg>
          </motion.div>)}
      </div>
    </div>;
};
interface FloatingShapeProps {
  shape: 'triangle' | 'square' | 'circle' | 'hexagon';
  size: number;
  position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  color?: string;
  delay?: number;
}
export const FloatingShape = ({
  shape,
  size,
  position,
  color = '#9CC31C',
  delay = 0
}: FloatingShapeProps) => {
  const shapeElements = {
    triangle: <polygon points={`${size / 2},0 0,${size} ${size},${size}`} fill={color} fillOpacity="0.2" data-id="element-123" />,
    square: <rect width={size} height={size} fill={color} fillOpacity="0.2" data-id="element-124" />,
    circle: <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} fillOpacity="0.2" data-id="element-125" />,
    hexagon: <path d={`M${size / 2},0 L${size},${size * 0.25} L${size},${size * 0.75} L${size / 2},${size} L0,${size * 0.75} L0,${size * 0.25} Z`} fill={color} fillOpacity="0.2" data-id="element-126" />
  };
  return <motion.div className="absolute z-0" style={{
    ...position
  }} animate={{
    y: [0, 15, 0],
    rotate: [0, 10, 0]
  }} transition={{
    duration: 8,
    repeat: Infinity,
    repeatType: 'reverse',
    ease: 'easeInOut',
    delay: delay
  }} data-id="element-127">
      <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg" data-id="element-128">
        {shapeElements[shape]}
      </svg>
    </motion.div>;
};
interface TechTagProps {
  name: string;
  logoUrl: string;
}
export const TechTag = ({
  name,
  logoUrl
}: TechTagProps) => {
  return <motion.div className="px-4 py-2 rounded-full bg-gradient-to-r from-white to-gray-100 text-gray-700 flex items-center gap-3 shadow-sm border border-gray-200" whileHover={{
    scale: 1.05,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    transition: {
      duration: 0.2
    }
  }} data-id="element-129">
      <img src={logoUrl} alt={`${name} logo`} className="w-6 h-6 object-contain" data-id="element-130" />
      <span className="text-sm font-medium" data-id="element-131">{name}</span>
    </motion.div>;
};